// ---------------------------------------------------------------------------
// TanStack Query hook for shared-services org-node data
// ---------------------------------------------------------------------------

import { useQuery } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getSharedApiBaseUrl } from '@/lib/config';

interface OrgNodeRaw {
  id: string;
  parent_id: string | null;
  org_type: string;
  name: string;
  is_active: boolean;
}

export interface OrgNode {
  id: string;
  name: string;
}

async function fetchOrgNodes(): Promise<OrgNode[]> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new Error('No auth session');

  const res = await fetch(`${getSharedApiBaseUrl()}/shared/public/org/nodes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ orgType: 'customer' }),
  });

  if (!res.ok) throw new Error(`Failed to fetch org nodes: HTTP ${res.status}`);

  const json = (await res.json()) as { data: OrgNodeRaw[] };
  return json.data
    .filter((n) => n.is_active)
    .map((n) => ({ id: n.id, name: n.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function useOrgNodes() {
  return useQuery({
    queryKey: ['org', 'nodes', 'customer'],
    queryFn: fetchOrgNodes,
    staleTime: 10 * 60_000,
  });
}
